// Email verification (Phase 17.D). Two modes by URL:
//  - with ?uid=&token=  → auto-verify on mount (POST /auth/register/verify),
//    then "verified → sign in".
//  - without            → a "resend verification link" form (generic 200).
// Mirrors PasswordReset's dual-mode-by-URL-param shape.

/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Brand, Kicker } from '../../components/shell';
import { resendVerification, verifyEmail } from '../../lib/auth';

export default function VerifyEmail() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const uid = params.get('uid');
  const token = params.get('token');
  const confirmMode = !!(uid && token);

  // confirmMode states: 'verifying' | 'done' | 'error'
  const [status, setStatus] = useState<'verifying' | 'done' | 'error'>('verifying');
  const ran = useRef(false);

  const [email, setEmail] = useState('');
  const [resent, setResent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!confirmMode || ran.current) return;
    ran.current = true;  // guard React StrictMode double-mount
    verifyEmail(uid!, token!)
      .then(() => setStatus('done'))
      .catch(() => setStatus('error'));
  }, [confirmMode, uid, token]);

  const submitResend = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await resendVerification(email.trim());
    } catch {
      // resend is anti-enumerating (always generic 200) — show the same panel.
    } finally {
      setResent(true);
      setSubmitting(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--paper)' }}>
      <header style={{ padding: '24px var(--pad-x)', borderBottom: 'var(--hairline)' }}>
        <div style={{ maxWidth: 'var(--max)', margin: '0 auto' }}><Brand onClick={() => navigate('/')} /></div>
      </header>
      <main style={{ flex: 1, display: 'grid', placeItems: 'center', padding: '80px var(--pad-x)' }}>
        <div style={{ width: '100%', maxWidth: 420 }}>
          <Kicker>VERIFY YOUR EMAIL</Kicker>

          {confirmMode ? (
            status === 'verifying' ? (
              <>
                <h1 className="page-title" style={{ margin: '8px 0 28px' }}><em>Verifying…</em></h1>
                <p className="serif-body" style={{ margin: 0, fontSize: 16, color: 'var(--ink-2)' }}>
                  Confirming your email — one moment.
                </p>
              </>
            ) : status === 'done' ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                <h1 className="page-title" style={{ margin: '8px 0 0' }}><em>Verified.</em></h1>
                <p className="serif-body" style={{ margin: 0, fontSize: 16 }}>
                  Your email is confirmed. Sign in to finish setting up your fund.
                </p>
                <button className="btn" onClick={() => navigate('/sign-in')}>
                  To sign in <span className="btn-arrow">→</span>
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                <h1 className="page-title" style={{ margin: '8px 0 0' }}><em>Link expired.</em></h1>
                <p className="serif-body" style={{ margin: 0, fontSize: 16, color: 'var(--ink-2)' }}>
                  This verification link is invalid or has expired. Request a fresh one below.
                </p>
                <button className="btn-link" onClick={() => navigate('/verify-email')}>
                  Resend verification link
                </button>
              </div>
            )
          ) : resent ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
              <h1 className="page-title" style={{ margin: '8px 0 0' }}><em>Sent.</em></h1>
              <p className="serif-body" style={{ margin: 0, fontSize: 16, color: 'var(--ink-2)' }}>
                If that account exists and is unverified, a new verification link has been sent.
              </p>
              <button className="btn" onClick={() => navigate('/sign-in')}>To sign in <span className="btn-arrow">→</span></button>
            </div>
          ) : (
            <>
              <h1 className="page-title" style={{ margin: '8px 0 28px' }}><em>Resend.</em></h1>
              <form onSubmit={submitResend} style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
                <div className="field">
                  <label className="field-label">Email</label>
                  <input className="field-input" type="email" value={email}
                         onChange={e => setEmail(e.target.value)} autoComplete="email" required autoFocus />
                </div>
                <div style={{ display: 'flex', gap: 24, alignItems: 'center' }}>
                  <button type="submit" className="btn" disabled={submitting}>
                    {submitting ? 'Working…' : 'Send verification link'}
                  </button>
                  <button type="button" className="btn-link" onClick={() => navigate('/sign-in')}>Back to sign in</button>
                </div>
              </form>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
